import { UserPlusIcon } from '@heroicons/react/24/outline'
import { OtpVerificationSettings, SplashScreenSettings } from 'components/settings';
import AuthenticationSettings from 'components/settings/AuthenticationSettings';
import { Button } from 'components/ui/button';
import { useMemo, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup'
import { onboardingSchema } from './validaitons';
import { useNotificationBar } from 'context/notificationContext';
import { useAddSettings, useSettingsData, useUpdateSettings } from 'hooks/settings';
import { MOCK_SITE_SETTINGS } from './settings-data';
import { useUploadFiles } from 'hooks/upload';
import { processImageValue } from './helper';
import { getImageSrc, getRelativeIconPath } from 'services/util';
import { OnboardingFormValues } from './interface';

const OnboardingSettings = () => {
    const notification = useNotificationBar()

    const { mutate: addSettings, isLoading: addLoading } = useAddSettings()
    const { mutate: updateSettings, isLoading: updateLoading } = useUpdateSettings()
    const { data: settingsData, isLoading } = useSettingsData()
    const uploadMutation = useUploadFiles()

    const methods = useForm<OnboardingFormValues>({
        mode: "onChange",
        defaultValues: {
            splash: {
                s_title: "",
                s_subTitle: "",
            },
            auth: {
                a_image: null,
                a_title: "",
                a_subTitle: "",
                a_ctaText: "",
            },
            otp: {
                v_image: null,
                v_title: "",
                v_ctaText: "",
                v_resend_otp_text: "",
                v_otp_time_text: "",
                v_otp_time: undefined as unknown as number
            },
        },
        resolver: yupResolver(onboardingSchema),
    },
    );

    const { handleSubmit, reset, setError, getValues } = methods;

    const mapDataToFormValues = (dataArray: any[]): OnboardingFormValues => {
        const formValues: OnboardingFormValues = {
            splash: {
                s_title: "",
                s_subTitle: "",
            },
            auth: {
                a_image: null,
                a_title: "",
                a_subTitle: "",
                a_ctaText: "",
            },
            otp: {
                v_image: null,
                v_title: "",
                v_ctaText: "",
                v_resend_otp_text: "",
                v_otp_time_text: "",
                v_otp_time: undefined as unknown as number
            },
        };

        const onboardingItem = dataArray.find((item) => item.slug === "onboarding_screen");

        if (onboardingItem?.settings) {
            const { settings } = onboardingItem;
            if (settings.splash_screen) {
                formValues.splash = {
                    s_title: settings.splash_screen.title || "",
                    s_subTitle: settings.splash_screen.sub_title || "",
                };
            }

            if (settings.authentication) {
                formValues.auth = {
                    a_image: settings.authentication.image ? getImageSrc(settings.authentication.image) : null,
                    a_title: settings.authentication.title || "",
                    a_subTitle: settings.authentication.sub_title || "",
                    a_ctaText: settings.authentication.cta_text || "",
                };
            }

            if (settings.otp) {
                formValues.otp = {
                    v_image: settings.otp.image ? getImageSrc(settings.otp.image) : null,
                    v_title: settings.otp.title || "",
                    v_ctaText: settings.otp.cta_text || "",
                    v_resend_otp_text: settings.otp.resend_otp_text || "",
                    v_otp_time_text: settings.otp.otp_time_text || "",
                    v_otp_time: settings.otp.otp_time || "",
                };
            }
        }

        return formValues;
    };

    const uploadImage = async (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('customPath', 'settings/images')

        const res = await uploadMutation.mutateAsync({
            data: formData,
        })

        return res?.data?.data?.key || ''
    }

    const formDefaultValues = useMemo(() => {
        if (!isLoading && settingsData?.data) {
            const dataArray = Array.isArray(settingsData.data) && settingsData.data.length > 0
                ? settingsData.data
                : MOCK_SITE_SETTINGS;
            return mapDataToFormValues(dataArray);
        }
        return mapDataToFormValues(MOCK_SITE_SETTINGS);
    }, [settingsData?.data, isLoading]);

    useEffect(() => {
        if (!isLoading) {
            reset(formDefaultValues);
        }
    }, [formDefaultValues, isLoading, reset]);

    const buildSiteSettingsPayload = (data: any, authImageKey: string, otpImageKey: string) => {
        return {
            slug: "onboarding_screen",
            settings: {
                splash_screen: {
                    title: data.splash.s_title,
                    sub_title: data.splash.s_subTitle,
                },
                authentication: {
                    image: getRelativeIconPath(authImageKey),
                    title: data.auth.a_title,
                    sub_title: data.auth.a_subTitle,
                    cta_text: data.auth.a_ctaText,
                },
                otp: {
                    // image: otpImageKey,
                     image: getRelativeIconPath(otpImageKey),
                    title: data.otp.v_title,
                    cta_text: data.otp.v_ctaText,
                    resend_otp_text: data.otp.v_resend_otp_text,
                    otp_time_text: data.otp.v_otp_time_text,
                    otp_time: data.otp.v_otp_time,
                },
            },
            status: 1,
            is_delete: 0
        };
    };

    const onSubmit = async (data: any) => {
        const hasExistingData = Array.isArray(settingsData?.data) && settingsData.data.length > 0;
        const existingOnboardingSetting = hasExistingData
            ? settingsData.data.find((item: any) => item.slug === "onboarding_screen")
            : null;

        const formValues = getValues();
        const authImageValue = formValues.auth?.a_image || data.auth?.a_image;
        const otpImageValue = formValues.otp?.v_image || data.otp?.v_image;

        let authImageKey = '';
        let otpImageKey = '';

        try {
            authImageKey = await processImageValue(
                authImageValue,
                uploadImage,
                existingOnboardingSetting?.settings?.authentication?.image
            );
        } catch (error) {
            console.error('Authentication image upload failed:', error);
            setError('auth.a_image', {
                type: 'upload',
                message: 'Failed to upload authentication image'
            });
            return;
        }

        try {
            otpImageKey = await processImageValue(
                otpImageValue,
                uploadImage,
                existingOnboardingSetting?.settings?.otp?.image
            );
        } catch (error) {
            setError('otp.v_image', {
                type: 'upload',
                message: 'Failed to upload OTP image'
            });
            return;
        }
        if (authImageKey === '[object File]' || otpImageKey === '[object File]' ||
            authImageKey?.includes('[object') || otpImageKey?.includes('[object')) {
            authImageKey = existingOnboardingSetting?.settings?.authentication?.image || '';
            otpImageKey = existingOnboardingSetting?.settings?.otp?.image || '';
        }

        const payload = buildSiteSettingsPayload(data, authImageKey, otpImageKey);
        if (existingOnboardingSetting?.id) {
            updateSettings(
                {
                    data: payload,
                    setting_id: existingOnboardingSetting.id
                },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: `Settings updated successfully`,
                        })
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: `Something went wrong`,
                        })
                    },
                }
            );
        } else {
            addSettings(
                { data: payload },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: `Settings created successfully`,
                        })
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: `Something went wrong`,
                        })
                    },
                }
            );
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className='w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md '>
                    <aside className="flex flex-auto items-center gap-2">
                        <UserPlusIcon className="h-6 w-6" />
                        <h4 className="text-xl">Onboarding Settings</h4>
                    </aside>
                    <div className="my-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                        <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
                            Splash Screen Settings
                        </h4>
                        <SplashScreenSettings />
                    </div>
                    <div className="my-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                        <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
                            Authentication Settings
                        </h4>
                        <AuthenticationSettings />
                    </div>
                    <div className="my-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                        <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
                            OTP Settings
                        </h4>
                        <OtpVerificationSettings />
                    </div>

                    <div className="flex justify-left my-6">
                        <Button color="blue" type='submit' isLoading={updateLoading || addLoading}>Save Settings</Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}

export default OnboardingSettings